// Shared contract for server-evaluated library browse filters (LG-248).
//
// The coaching dashboard's filter builder produces a flat tree — rules and
// one-level groups combined left-to-right with and/or, no precedence. In
// persisted mode that tree is sent to `browseLearningItems` and compiled to
// SQL (`browse-filter-sql.ts`), so filtering is correct across the whole
// corpus rather than just loaded pages. This module is the wire contract
// both sides import; it must stay free of React and database imports.

export const BROWSE_FILTER_FIELDS = [
  // Matches any of the problem's `topic / subtopic` pairs (LG-542) — the
  // same stacked pairs the Topic / Sub column shows, not the track.
  'topic',
  'title',
  'extId',
  'elo',
  'difficulty',
  'plan',
  'source',
] as const

export type BrowseFilterField = (typeof BROWSE_FILTER_FIELDS)[number]

export interface BrowseFilterRule {
  connector: 'where' | 'and' | 'or'
  field: BrowseFilterField
  condition: string
  value: string | number
}

export interface BrowseFilterGroup {
  connector: 'and' | 'or'
  rules: BrowseFilterRule[]
}

export type BrowseFilterNode = BrowseFilterRule | BrowseFilterGroup

export const browseCondsByField: Record<BrowseFilterField, readonly string[]> =
  {
    topic: ['contains', 'is', 'is not'],
    title: ['contains', 'is', 'is not'],
    extId: ['contains', 'is', 'is not'],
    elo: ['>', '<', '=', '≥', '≤'],
    difficulty: ['is', 'is not'],
    plan: ['in plan', 'not in plan'],
    source: ['is', 'is not'],
  }

export const MAX_BROWSE_FILTER_NODES = 20
export const MAX_BROWSE_FILTER_GROUP_RULES = 20
export const MAX_BROWSE_FILTER_VALUE_LENGTH = 200

// The single owner of the difficulty vocabulary: UI labels on one side,
// DifficultyLevel row names on the other. The SQL compiler, the DTO
// mapper, and the filter dropdown all derive from this list so a filter
// can never disagree with the displayed column.
export const BROWSE_DIFFICULTIES = [
  { label: 'Easy', dbName: 'easy' },
  { label: 'Med', dbName: 'medium' },
  { label: 'Hard', dbName: 'hard' },
] as const

export type BrowseDifficultyLabel =
  (typeof BROWSE_DIFFICULTIES)[number]['label']

export const browseDifficultyDbName = (value: string): string | undefined =>
  BROWSE_DIFFICULTIES.find(
    (entry) => entry.label.toLowerCase() === value.toLowerCase(),
  )?.dbName

export const browseDifficultyLabel = (
  dbName: string,
): BrowseDifficultyLabel | undefined =>
  BROWSE_DIFFICULTIES.find((entry) => entry.dbName === dbName.toLowerCase())
    ?.label

export const isBrowseFilterGroup = (
  node: BrowseFilterNode,
): node is BrowseFilterGroup => 'rules' in node

// The client filter builder is a superset of the wire contract: it also has
// mock-only fields (company) and lets rules sit half-edited (a cleared ELO
// value). Sanitizing keeps only complete rules on server-supported fields
// with known conditions, drops groups left empty, and clamps sizes.
interface CandidateRule {
  connector: 'where' | 'and' | 'or'
  field: string
  condition: string
  value: unknown
}

interface CandidateGroup {
  connector: 'and' | 'or'
  rules: CandidateRule[]
}

const isServerField = (field: string): field is BrowseFilterField =>
  (BROWSE_FILTER_FIELDS as readonly string[]).includes(field)

function sanitizeRule(rule: CandidateRule): BrowseFilterRule | null {
  if (!isServerField(rule.field)) return null
  if (!browseCondsByField[rule.field].includes(rule.condition)) return null
  const { value } = rule
  if (typeof value !== 'string' && typeof value !== 'number') return null
  if (
    rule.field === 'difficulty' &&
    (typeof value !== 'string' || browseDifficultyDbName(value) === undefined)
  ) {
    return null
  }
  if (rule.field === 'elo') {
    const numeric = typeof value === 'number' ? value : Number(value)
    if (
      (typeof value === 'string' && value.trim() === '') ||
      !Number.isFinite(numeric)
    ) {
      return null
    }
    return {
      connector: rule.connector,
      field: rule.field,
      condition: rule.condition,
      value: numeric,
    }
  }
  return {
    connector: rule.connector,
    field: rule.field,
    condition: rule.condition,
    value,
  }
}

// Size limits are NOT enforced here: the filter builder disables adding
// rules past the caps and the tRPC zod schema hard-rejects oversized
// trees, so silently clamping would only hide a bug as wrong results.
export function sanitizeBrowseFilters(
  nodes: readonly (CandidateRule | CandidateGroup)[],
): BrowseFilterNode[] {
  const sanitized: BrowseFilterNode[] = []
  for (const node of nodes) {
    if ('rules' in node) {
      const rules = node.rules
        .map(sanitizeRule)
        .filter((rule): rule is BrowseFilterRule => rule !== null)
      if (rules.length > 0) sanitized.push({ connector: node.connector, rules })
      continue
    }
    const rule = sanitizeRule(node)
    if (rule !== null) sanitized.push(rule)
  }
  return sanitized
}
